import axios, { AxiosRequestConfig, CancelToken } from 'axios'

import type { RawPresetsData } from '../../components/credit/EsgIndexLab/params/schema'
import type {
  ApiEnvelope,
  AssessmentQueue,
  BuildResult,
  ControversyEvent,
  ControversyEventInput,
  EsgCompany,
  EtfRequest,
  ExplainData,
  ExportFile,
  HistoryData,
  IssuerMapRow,
  IssuerOverride,
  MethodologyFile,
  NccReducerRow,
  ParamsFormat,
  ParamsImportResult,
  ReferenceSet,
  ReferenceUploadResult,
  RestrictionCandidates,
  RestrictionInput,
  RestrictionRow,
  Scenario,
  ScenarioInput,
  SchemesData,
  ScoreUploadMeta,
  ScoreUploadResult,
  ScoreVersion,
  SectorMapRow,
  SimParams,
  SimResult,
  SnapshotDoc,
  SnapshotListItem,
  VintagePut,
  VintageRow,
} from '../../components/credit/EsgIndexLab/types'
import { getFileNameFromHeaders } from '../../utils/files'

// Client of the ESG Index Lab (layer 0: HTTP only, no React).
//
// Absolute host + Bearer from /api/auth/token, like the other credit pages
// (`/api/compass/...` is not routed in this app). REACT_APP_ESG_LAB_HOST
// exists only so a local harness can point at a stub.
const HOST = process.env.REACT_APP_ESG_LAB_HOST ?? 'https://api.jgp.com.br'
export const ESG_LAB_BASE = `${HOST}/credit/api/v1/esg-index-lab`

const TOKEN_TTL_MS = 5 * 60_000
let tokenCache: { token: string; expiresAt: number } | null = null
let inflightTokenRequest: Promise<string | null> | null = null

const fetchFreshToken = async (): Promise<string | null> => {
  try {
    const res = await fetch('/api/auth/token')
    if (!res.ok) return null
    const { token } = await res.json()
    return typeof token === 'string' ? token : null
  } catch {
    return null
  }
}

// Cache + de-dup: the page fires several calls at once on load.
const getToken = async (): Promise<string | null> => {
  const now = Date.now()
  if (tokenCache && now < tokenCache.expiresAt) return tokenCache.token
  if (inflightTokenRequest) return inflightTokenRequest
  inflightTokenRequest = fetchFreshToken().then((token) => {
    if (token) tokenCache = { token, expiresAt: Date.now() + TOKEN_TTL_MS }
    inflightTokenRequest = null
    return token
  })
  return inflightTokenRequest
}

// Called on logout (UserContext): a token must not outlive the session.
export const clearEsgIndexLabToken = (): void => {
  tokenCache = null
  inflightTokenRequest = null
}

// Never send a request without Authorization: no token, no request.
const authHeaders = async (): Promise<Record<string, string>> => {
  const token = await getToken()
  if (!token) throw new Error('ESG Index Lab: no auth token')
  return { Authorization: `Bearer ${token}` }
}

// Dedicated instance, so the global axios interceptors stay clean.
export const esgLabHttp = axios.create({ baseURL: ESG_LAB_BASE })

// On 401: drop the cached token, fetch a fresh one and retry ONCE.
esgLabHttp.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status
    const cfg = err?.config
    if (status === 401 && cfg && !cfg._retry) {
      tokenCache = null
      cfg._retry = true
      const token = await getToken()
      if (!token) throw new Error('ESG Index Lab: no auth token')
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${token}` }
      return esgLabHttp.request(cfg)
    }
    return Promise.reject(err)
  }
)

// axios 0.21 has no AbortSignal support: bridge it to a CancelToken.
const toCancelToken = (signal?: AbortSignal): CancelToken | undefined => {
  if (!signal) return undefined
  return new axios.CancelToken((cancel) => {
    if (signal.aborted) cancel('aborted')
    else signal.addEventListener('abort', () => cancel('aborted'))
  })
}

export const isCancel = (e: unknown): boolean =>
  axios.isCancel(e) || (e instanceof Error && e.name === 'AbortError')

export interface CallOpts {
  signal?: AbortSignal
}

const request = async <T>(
  cfg: AxiosRequestConfig,
  opts?: CallOpts
): Promise<T> => {
  const { data } = await esgLabHttp.request<ApiEnvelope<T>>({
    ...cfg,
    headers: { ...(cfg.headers || {}), ...(await authHeaders()) },
    cancelToken: toCancelToken(opts?.signal),
  })
  return data.data
}

const enc = encodeURIComponent

// `etf` (engine 0.9.0): sent only when the engine serves etf_presets.
export type SimulateBody = SimParams & {
  ref_date: string
  etf?: EtfRequest | null
}

// /simulate/history: the ETF layer travels with the call options.
export interface HistoryCallOpts extends CallOpts {
  etf?: EtfRequest | null
}

// The whole surface the page uses (local tests implement it with recorded
// responses).
export interface EsgLabApi {
  listSnapshots(): Promise<SnapshotListItem[]>
  getSnapshot(refDate: string): Promise<SnapshotDoc>
  buildSnapshot(
    refDate: string,
    kind?: 'adhoc' | 'month_end',
    force?: boolean
  ): Promise<BuildResult>
  // raw shape; usePresets normalises it (params/schema.ts)
  getPresets(): Promise<RawPresetsData>
  simulate(body: SimulateBody, opts?: CallOpts): Promise<SimResult>
  explain(
    refDate: string,
    params: SimParams,
    code: string,
    opts?: CallOpts
  ): Promise<ExplainData>
  listScores(): Promise<ScoreVersion[]>
  // v1.2: the upload form also sends base_year, kind, label, official
  uploadScores(
    file: File,
    asOf: string,
    meta?: ScoreUploadMeta
  ): Promise<ScoreUploadResult>
  getIssuerMap(refDate: string, onlyUnmatched: boolean): Promise<IssuerMapRow[]>
  putIssuerMap(
    issuerCnpj: string,
    companyKey: string | null
  ): Promise<IssuerMapRow>
  searchCompanies(q: string): Promise<EsgCompany[]>
  getSectorMap(): Promise<SectorMapRow[]>
  putSectorMap(row: {
    source: string
    raw_sector: string
    unified_sector: string
  }): Promise<SectorMapRow>
  getIssuerOverrides(): Promise<IssuerOverride[]>
  putIssuerOverride(row: IssuerOverride): Promise<IssuerOverride>
  deleteIssuerOverride(cnpj: string): Promise<void>
  listScenarios(): Promise<Scenario[]>
  createScenario(s: ScenarioInput): Promise<Scenario>
  updateScenario(id: string, s: Partial<ScenarioInput>): Promise<Scenario>
  deleteScenario(id: string): Promise<void>
  // `etf` (engine 0.9.0 only): five more sheets with the ETF layer
  exportXlsx(
    refDate: string,
    params: SimParams,
    name: string,
    etf?: EtfRequest | null
  ): Promise<ExportFile>
  // ---- v1.1 (overview §D; engine report 20-23)
  // the current params on every ready month-end (no universe, ever)
  simulateHistory(
    params: SimParams,
    refDates?: string[] | null,
    opts?: HistoryCallOpts
  ): Promise<HistoryData>
  // `etf` (engine 0.9.0 only): a top-level "etf" key in the Lab file
  exportParams(
    params: SimParams,
    format: ParamsFormat,
    presetId: string | null,
    etf?: EtfRequest | null
  ): Promise<ExportFile>
  importParams(
    format: ParamsFormat,
    payload: Record<string, unknown>,
    presetId?: string | null
  ): Promise<ParamsImportResult>
  listReferenceSets(): Promise<ReferenceSet[]>
  uploadReferenceSet(file: File, name: string): Promise<ReferenceUploadResult>
  deleteReferenceSet(
    setId: string
  ): Promise<{ set_id?: string; deleted: number }>
  // ---- v1.2 (overview §F; engine deviations 14-16; data report routes).
  // Every `params` here is a FULL params object built from a /presets
  // preset: a v1.1-shaped object without the v1.2 keys would silently get
  // the ESG deck's layer (engine deviation 1).
  simulateSchemes(
    refDate: string,
    params: SimParams,
    schemes?: string[] | null,
    opts?: CallOpts
  ): Promise<SchemesData>
  assessmentQueue(
    refDate: string,
    presetId: string | null,
    params: SimParams,
    opts?: CallOpts
  ): Promise<AssessmentQueue>
  exportMethodology(
    refDate: string,
    params: SimParams
  ): Promise<MethodologyFile>
  listRestrictions(q?: {
    status?: string | null
    theme?: string | null
    q?: string | null
  }): Promise<RestrictionRow[]>
  restrictionCandidates(
    refDate: string | null,
    universe?: string | null
  ): Promise<RestrictionCandidates>
  createRestriction(row: RestrictionInput): Promise<RestrictionRow>
  updateRestriction(id: string, row: RestrictionInput): Promise<RestrictionRow>
  deleteRestriction(
    id: string
  ): Promise<{ id?: string; deleted?: boolean | number }>
  listEvents(q?: {
    status?: string | null
    company?: string | null
  }): Promise<ControversyEvent[]>
  createEvent(row: ControversyEventInput): Promise<ControversyEvent>
  updateEvent(
    eventId: string,
    row: ControversyEventInput
  ): Promise<ControversyEvent>
  deleteEvent(
    eventId: string
  ): Promise<{ event_id?: string; deleted?: boolean | number }>
  listVintages(): Promise<VintageRow[]>
  putVintage(asOf: string, row: VintagePut): Promise<VintageRow>
  // v1.3: the official formula in the table shape (read-only: the PUT
  // answers 409)
  getNccReducer(): Promise<NccReducerRow[]>
}

// FileResponse (no envelope): the blob, the Content-Disposition name and
// the response headers (axios lower-cases them; a header the server does
// not list in Access-Control-Expose-Headers is absent cross-origin).
const fileRequestRaw = async (
  cfg: AxiosRequestConfig
): Promise<ExportFile & { headers: Record<string, string> }> => {
  const res = await esgLabHttp.request<Blob>({
    ...cfg,
    responseType: 'blob',
    headers: { ...(cfg.headers || {}), ...(await authHeaders()) },
  })
  const headers = (res.headers ?? {}) as Record<string, string>
  const filename = getFileNameFromHeaders(headers)
  return {
    filename: filename ? decodeURI(filename) : null,
    blob: res.data,
    headers,
  }
}

const fileRequest = async (cfg: AxiosRequestConfig): Promise<ExportFile> => {
  const { filename, blob } = await fileRequestRaw(cfg)
  return { filename, blob }
}

// X-Methodology-Hash / X-Methodology-Warnings (engine deviation 16).
export const methodologyHeaders = (
  headers: Record<string, string>
): { hash: string | null; warnings: number | null } => {
  const get = (k: string) => headers[k] ?? headers[k.toLowerCase()] ?? null
  const hash = get('X-Methodology-Hash')
  const w = get('X-Methodology-Warnings')
  const n = w === null ? NaN : Number(w)
  return { hash: hash || null, warnings: Number.isFinite(n) ? n : null }
}

export const liveEsgLabApi: EsgLabApi = {
  listSnapshots: () => request({ url: '/snapshots', method: 'GET' }),
  getSnapshot: (refDate) =>
    request({ url: `/snapshots/${enc(refDate)}`, method: 'GET' }),
  buildSnapshot: (refDate, kind = 'adhoc', force = false) =>
    request({
      url: `/snapshots/${enc(refDate)}/build`,
      method: 'POST',
      params: { kind, force },
    }),
  getPresets: () => request({ url: '/presets', method: 'GET' }),
  simulate: (body, opts) =>
    request({ url: '/simulate', method: 'POST', data: body }, opts),
  explain: (refDate, params, code, opts) =>
    request(
      {
        url: '/explain',
        method: 'POST',
        data: { ref_date: refDate, params, code },
      },
      opts
    ),
  listScores: () => request({ url: '/esg/scores', method: 'GET' }),
  uploadScores: (file, asOf, meta) => {
    const form = new FormData()
    form.append('file', file)
    form.append('as_of', asOf)
    if (meta?.base_year !== undefined && meta.base_year !== null)
      form.append('base_year', String(meta.base_year))
    if (meta?.kind) form.append('kind', meta.kind)
    if (meta?.label) form.append('label', meta.label)
    if (meta?.official !== undefined)
      form.append('official', meta.official ? 'true' : 'false')
    return request({ url: '/esg/scores/upload', method: 'POST', data: form })
  },
  getIssuerMap: (refDate, onlyUnmatched) =>
    request({
      url: '/issuer-map',
      method: 'GET',
      params: { ref_date: refDate, only_unmatched: onlyUnmatched },
    }),
  putIssuerMap: (issuerCnpj, companyKey) =>
    request({
      url: '/issuer-map',
      method: 'PUT',
      data: { issuer_cnpj: issuerCnpj, company_key: companyKey },
    }),
  searchCompanies: (q) =>
    request({ url: '/esg/companies', method: 'GET', params: { q } }),
  getSectorMap: () => request({ url: '/sector-map', method: 'GET' }),
  putSectorMap: (row) =>
    request({ url: '/sector-map', method: 'PUT', data: row }),
  getIssuerOverrides: () =>
    request({ url: '/issuer-overrides', method: 'GET' }),
  putIssuerOverride: (row) =>
    request({ url: '/issuer-overrides', method: 'PUT', data: row }),
  deleteIssuerOverride: (cnpj) =>
    request({ url: `/issuer-overrides/${enc(cnpj)}`, method: 'DELETE' }),
  listScenarios: () => request({ url: '/scenarios', method: 'GET' }),
  createScenario: (s) =>
    request({ url: '/scenarios', method: 'POST', data: s }),
  updateScenario: (id, s) =>
    request({ url: `/scenarios/${enc(id)}`, method: 'PUT', data: s }),
  deleteScenario: (id) =>
    request({ url: `/scenarios/${enc(id)}`, method: 'DELETE' }),
  exportXlsx: (refDate, params, name, etf) =>
    fileRequest({
      url: '/export',
      method: 'POST',
      data: { ref_date: refDate, params, name, ...(etf ? { etf } : {}) },
    }),
  simulateHistory: (params, refDates, opts) =>
    request(
      {
        url: '/simulate/history',
        method: 'POST',
        data: {
          params,
          ...(refDates?.length ? { ref_dates: refDates } : {}),
          with_reference: true,
          ...(opts?.etf ? { etf: opts.etf } : {}),
        },
      },
      opts
    ),
  exportParams: (params, format, presetId, etf) =>
    fileRequest({
      url: '/params/export',
      method: 'POST',
      data: {
        params,
        format,
        ...(presetId ? { preset_id: presetId } : {}),
        ...(etf ? { etf } : {}),
      },
    }),
  importParams: (format, payload, presetId) =>
    request({
      url: '/params/import',
      method: 'POST',
      data: { format, payload, ...(presetId ? { preset_id: presetId } : {}) },
    }),
  listReferenceSets: () =>
    request({ url: '/reference-portfolios', method: 'GET' }),
  // plain multipart (engine report 23): `file` (CSV ref_date;code;weight;
  // entry_date) + `name`
  uploadReferenceSet: (file, name) => {
    const form = new FormData()
    form.append('file', file)
    form.append('name', name)
    return request({
      url: '/reference-portfolios/upload',
      method: 'POST',
      data: form,
    })
  },
  deleteReferenceSet: (setId) =>
    request({ url: `/reference-portfolios/${enc(setId)}`, method: 'DELETE' }),
  // ---- v1.2
  simulateSchemes: (refDate, params, schemes, opts) =>
    request(
      {
        url: '/simulate/schemes',
        method: 'POST',
        data: {
          ref_date: refDate,
          params,
          ...(schemes?.length ? { schemes } : {}),
        },
      },
      opts
    ),
  // the queue deep-merges `params` over `preset_id` server-side; the page
  // sends its full params, so the merge is a no-op
  assessmentQueue: (refDate, presetId, params, opts) =>
    request(
      {
        url: '/esg/assessment-queue',
        method: 'GET',
        params: {
          ref_date: refDate,
          universe: params.universe,
          ...(presetId ? { preset_id: presetId } : {}),
          params: JSON.stringify(params),
        },
      },
      opts
    ),
  exportMethodology: async (refDate, params) => {
    const f = await fileRequestRaw({
      url: '/export/methodology',
      method: 'POST',
      data: { ref_date: refDate, params, lang: 'pt' },
    })
    return {
      filename: f.filename,
      blob: f.blob,
      ...methodologyHeaders(f.headers),
    }
  },
  listRestrictions: (q) =>
    request({
      url: '/restrictions',
      method: 'GET',
      params: {
        ...(q?.status ? { status: q.status } : {}),
        ...(q?.theme ? { theme: q.theme } : {}),
        ...(q?.q ? { q: q.q } : {}),
      },
    }),
  restrictionCandidates: (refDate, universe) =>
    request({
      url: '/restrictions/candidates',
      method: 'GET',
      params: {
        ...(refDate ? { ref_date: refDate } : {}),
        ...(universe ? { universe } : {}),
      },
    }),
  createRestriction: (row) =>
    request({ url: '/restrictions', method: 'POST', data: row }),
  updateRestriction: (id, row) =>
    request({ url: `/restrictions/${enc(id)}`, method: 'PUT', data: row }),
  deleteRestriction: (id) =>
    request({ url: `/restrictions/${enc(id)}`, method: 'DELETE' }),
  listEvents: (q) =>
    request({
      url: '/controversy-events',
      method: 'GET',
      params: {
        ...(q?.status ? { status: q.status } : {}),
        ...(q?.company ? { company: q.company } : {}),
      },
    }),
  createEvent: (row) =>
    request({ url: '/controversy-events', method: 'POST', data: row }),
  updateEvent: (eventId, row) =>
    request({
      url: `/controversy-events/${enc(eventId)}`,
      method: 'PUT',
      data: row,
    }),
  deleteEvent: (eventId) =>
    request({ url: `/controversy-events/${enc(eventId)}`, method: 'DELETE' }),
  listVintages: () => request({ url: '/esg/vintages', method: 'GET' }),
  putVintage: (asOf, row) =>
    request({ url: `/esg/vintages/${enc(asOf)}`, method: 'PUT', data: row }),
  getNccReducer: () => request({ url: '/esg/ncc-reducer', method: 'GET' }),
}

export const loadEsgLabApi = (): Promise<EsgLabApi> =>
  Promise.resolve(liveEsgLabApi)
